import { useCallback, useRef, useState } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";

const BEFORE_IMG =
  "https://images.pexels.com/photos/15185306/pexels-photo-15185306/free-photo-of-view-of-autumnal-leaves-floating-in-a-dirty-swimming-pool.jpeg?auto=compress&cs=tinysrgb&w=1600";
const AFTER_IMG = "/images/cp-story.webp";

export default function BeforeAfterSection() {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(55);

  const updateFromClientX = useCallback((clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(96, Math.max(4, p)));
  }, []);

  const onPointerDown = (e) => {
    e.preventDefault();
    updateFromClientX(e.clientX);
    const move = (ev) => updateFromClientX(ev.clientX);
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <section id="vorher-nachher" data-testid="before-after-section" className="relative py-20 sm:py-28">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-center max-w-2xl mx-auto"
        >
          <p className="text-xs uppercase tracking-[0.32em] font-mono text-cyan-400 font-semibold">Vorher / Nachher</p>
          <h2 className="mt-4 font-medium tracking-tight leading-tight text-3xl sm:text-5xl text-white">
            Die Verwandlung <span className="italic text-cyan-300">zum Traumpool.</span>
          </h2>
          <p className="mt-4 text-slate-400 text-sm sm:text-base font-light">
            Ziehen Sie den Regler — aus vernachlässigt wird unverwechselbar.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          ref={containerRef}
          data-testid="before-after-slider"
          onPointerDown={onPointerDown}
          className="mt-12 relative aspect-[16/10] sm:aspect-[16/8] rounded-2xl overflow-hidden border border-white/10 select-none cursor-ew-resize touch-none shadow-[0_40px_100px_-30px_rgba(0,229,255,0.15)]"
        >
          <img
            src={AFTER_IMG}
            alt="Nachher: beleuchteter Traumpool am Abend"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            draggable="false"
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
          >
            <img
              src={BEFORE_IMG}
              alt="Vorher: vernachlässigter Pool"
              className="absolute inset-0 w-full h-full object-cover saturate-[0.7]"
              draggable="false"
            />
          </div>

          <span data-testid="before-label" className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.22em] font-mono font-bold bg-[#050B14]/75 backdrop-blur px-3.5 py-1.5 rounded-full border border-white/15 text-slate-200">
            Vorher
          </span>
          <span data-testid="after-label" className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.22em] font-mono font-bold bg-cyan-400/90 px-3.5 py-1.5 rounded-full text-[#050B14]">
            Nachher
          </span>

          <div
            data-testid="before-after-handle"
            className="absolute top-0 bottom-0 pointer-events-none"
            style={{ left: `${pos}%` }}
          >
            <div className="absolute top-0 bottom-0 -translate-x-1/2 w-[2px] bg-white/90 shadow-[0_0_16px_rgba(0,229,255,0.6)]" />
            <div className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white text-[#050B14] flex items-center justify-center shadow-[0_0_24px_rgba(0,229,255,0.5)]">
              <MoveHorizontal className="w-5 h-5" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
